import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import type { HeroFlowStep } from "@/components/hero/heroSlideCopy";

interface HeroJobFlowProps {
  steps: HeroFlowStep[];
  current: number;
  ariaLabel: string;
  onSelect: (step: number) => void;
}

/** One job from enquiry to paid invoice, one step card at a time. */
export function HeroJobFlow({ steps, current, ariaLabel, onSelect }: HeroJobFlowProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const step = steps[current] ?? steps[0];

  // Keep the active chip visible on narrow screens without scrolling the page.
  useEffect(() => {
    const list = listRef.current;
    const chip = list?.children[current] as HTMLElement | undefined;
    if (!list || !chip || list.scrollWidth <= list.clientWidth) return;
    list.scrollTo({ left: chip.offsetLeft - 8, behavior: "smooth" });
  }, [current]);

  if (!step) return null;

  return (
    <div className="hero-flow" role="group" aria-label={ariaLabel} data-testid="hero-job-flow">
      <ol ref={listRef} className="hero-flow-steps">
        {steps.map((item, index) => (
          <li key={item.label}>
            <button
              type="button"
              className={cn("hero-flow-step", index <= current && "is-done")}
              aria-current={index === current ? "step" : undefined}
              onClick={() => onSelect(index)}
              data-testid={`hero-flow-step-${index}`}
            >
              <b aria-hidden="true">{index + 1}</b>
              {item.label}
            </button>
          </li>
        ))}
      </ol>

      <article key={current} className="hero-flow-card" data-testid="hero-flow-card">
        <header>
          <strong>{step.title}</strong>
          <span className={cn("hero-flow-tag", step.ok && "is-ok")}>{step.tag}</span>
        </header>
        <dl>
          {step.rows.map((row) => (
            <div key={row.k} className={cn(row.total && "is-total")}>
              <dt>{row.k}</dt>
              <dd>{row.v}</dd>
            </div>
          ))}
        </dl>
        <p>{step.benefit}</p>
      </article>
    </div>
  );
}
